import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Site } from 'src/app/bo/site';
import { DEFAULT_SHIFT_CONFIG, DrillActivityService } from 'src/app/services/drill-activity.service';
import { HttpService } from 'src/app/services/http.service';
import { FeatureFlagService } from 'src/app/services/new_services/feature-flag.service';
import { ShiftConfigService } from 'src/app/services/shift-config.service';
import { BlastRow, DrillingSession, ShiftConfig, shiftConfigFromDto } from 'src/app/shared/models/drilling.models';
import { DrillColorBy, PeriodBounds, TimeScale } from '../planning.types';
import { DrillGridComponent } from './ui/drill-grid/drill-grid.component';

/** Ce que le chargement doit connaître d'un site : sa config de shifts et son fuseau. */
interface SiteContext {
  siteId: number;
  config: ShiftConfig;
  timezone?: string;
}

/**
 * Planning du forage : charge les tirs du site sur la période reçue et les
 * affiche dans la grille. La barre de contrôles appartient au conteneur.
 */
@Component({
  selector: 'app-drill-planning',
  standalone: true,
  templateUrl: './drill-planning.component.html',
  styleUrls: ['./drill-planning.component.scss'],
  imports: [TranslateModule, DrillGridComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrillPlanningComponent {
  readonly siteId    = input.required<number>();
  readonly bounds    = input.required<PeriodBounds>();
  readonly colorBy   = input<DrillColorBy>('rig');
  readonly timeScale = input<TimeScale>('week');

  private readonly drillActivity      = inject(DrillActivityService);
  private readonly shiftConfigService = inject(ShiftConfigService);
  private readonly http               = inject(HttpService);
  private readonly featureFlags       = inject(FeatureFlagService);
  private readonly router             = inject(Router);
  private readonly destroyRef         = inject(DestroyRef);

  protected readonly shiftConfig = signal<ShiftConfig>(DEFAULT_SHIFT_CONFIG);
  protected readonly blasts      = signal<BlastRow[]>([]);
  protected readonly loading     = signal(false);

  constructor() {
    const bounds$ = toObservable(this.bounds);

    // Chaque niveau est un switchMap : changer de site annule le contexte et les lignes
    // encore en vol pour l'ancien site, changer de période annule seulement les lignes.
    // Les erreurs sont absorbées au plus près pour ne jamais terminer le flux.
    toObservable(this.siteId)
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.shiftConfig.set(DEFAULT_SHIFT_CONFIG);
        }),
        switchMap(siteId => this.loadSiteContext(siteId)),
        tap(context => this.shiftConfig.set(context.config)),
        switchMap(context => bounds$.pipe(
          tap(() => this.loading.set(true)),
          switchMap(bounds => this.drillActivity
            .getBlastRowsForWeek(context.siteId, bounds, context.config, context.timezone)
            .pipe(catchError(() => of([] as BlastRow[])))),
        )),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(rows => {
        this.blasts.set(rows);
        this.loading.set(false);
      });
  }

  /**
   * La config de shifts détermine la colonne de chaque session et le fuseau du site
   * son jour local : les deux doivent être connus avant de répartir les données.
   */
  private loadSiteContext(siteId: number): Observable<SiteContext> {
    return forkJoin({
      config: this.shiftConfigService.getShiftConfig(siteId).pipe(
        map(dto => shiftConfigFromDto(dto)),
        catchError(() => of(DEFAULT_SHIFT_CONFIG)),
      ),
      timezone: this.http.get<Site>(`Sites/${siteId}`).pipe(
        map(site => site?.timezone ?? undefined),
        catchError(() => of(undefined)),
      ),
    }).pipe(map(({ config, timezone }) => ({ siteId, config, timezone })));
  }

  /** Ouvre le tir dans l'écran site actif pour ce site, sur la vue forage de l'onglet Holes. */
  protected openBlast(blast: BlastRow): void {
    const siteId = this.siteId();
    if (this.featureFlags.isLegacySiteEnabled(siteId)) {
      this.router.navigate(
        ['mysite', siteId, 'myprojects', blast.projectId],
        { queryParams: { blastId: blast.blastId, tab: 'drilling' } },
      );
      return;
    }
    this.router.navigate(['/site', siteId], {
      queryParams: {
        projectId: blast.projectId,
        blastId:   blast.blastId,
        nodeType:  'blast',
        nodeValue: blast.label,
        tab:       'holes',
        view:      'drilling',
      },
    });
  }

  protected openSession(session: DrillingSession): void {
    const blast = this.blasts().find(b => b.blastId === +session.blastId);
    if (blast) { this.openBlast(blast); }
  }
}
